class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // The #cart-icon-bubble element is rendered by Dawn's default header
    // (sections/header.liquid). Once we disabled that header in
    // header-group.json, this element isn't in the DOM — guard against
    // null so the constructor doesn't throw and break the custom element.
    // The peachy-mini-header has its own cart toggle wired separately.
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    // Restore focus to the trigger without scrolling the page. Default
    // .focus() behavior scrolls the focused element into view, which jumps
    // the page back to the top when the trigger was the header cart icon.
    removeTrapFocus();
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus({ preventScroll: true });
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // openDrawer (default true): whether to slide the drawer open after
  // rendering. Background refreshes (gift reconcile on page load, cart-line
  // edits in an already-open drawer) pass `false` so they NEVER force the
  // drawer open — a forced open on load adds `overflow-hidden` to <body> and
  // focuses the drawer (bottom of the DOM), which scroll-locks the page at
  // the footer. Only explicit user actions (cart icon, add-to-cart) open it.
  renderContents(parsedState, openDrawer = true) {
    if (!parsedState) return;
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    // Each render bumps this token; an in-flight fallback refetch from an
    // OLDER render aborts instead of overwriting newer markup with stale
    // line keys/indices.
    this._renderToken = (this._renderToken || 0) + 1;
    let renderFailed = false;
    this.getSectionsToRender().forEach((section) => {
      // cart-icon-bubble is one of the sections returned here, but its
      // host element is rendered by Dawn's header (now disabled), so
      // it's not in the DOM. Skip silently rather than throw on
      // .innerHTML of null.
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHtml) return;
      // A malformed section payload must never leave the drawer as a
      // blank full-viewport overlay (it covers the page while open).
      try {
        const newInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (newInnerHTML === null) throw new Error('selector missing in section payload');
        sectionElement.innerHTML = newInnerHTML;
      } catch (e) {
        console.warn('[cart-drawer] render failed for section', section.id, e);
        renderFailed = true;
      }
    });
    // One recovery refetch per render, no matter how many sections failed.
    if (renderFailed) this.refreshFromServer();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      if (openDrawer !== false) this.open();
    });
  }

  // Last-resort repaint from the live cart, so a failed render never
  // strands the customer on a broken drawer. Single-flight; aborts if a
  // newer render lands first; re-binds the overlay close handler that the
  // innerHTML swap destroys.
  refreshFromServer() {
    if (this._refreshing) return;
    this._refreshing = true;
    const token = this._renderToken;
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        if (token !== this._renderToken) return; // superseded by a newer render
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('#CartDrawer');
        const target = document.getElementById('CartDrawer');
        if (!source || !target) throw new Error('no #CartDrawer in refetched section');
        target.innerHTML = source.innerHTML;
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
      })
      .catch((e) => {
        // Log loudly — a silent failure here is an undiagnosable broken
        // drawer. The close button in the drawer markup still works.
        console.warn('[cart-drawer] server repaint failed', e);
      })
      .finally(() => {
        this._refreshing = false;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
